// Versioned JSON-based localStorage wrapper.

import { STORAGE_PREFIX } from '../config';
import type { Action } from '../input/input';
import { DEFAULT_KEYMAP } from '../input/input';
import type { AudioSettings } from '../audio/audio';
import { DEFAULT_AUDIO_SETTINGS } from '../audio/audio';
import type { PostFxSettings } from '../render/postfx';
import { DEFAULT_POSTFX } from '../render/postfx';
import type { ThemePalette } from '../render/palettes';
import type { RenderOptions } from '../render/renderer';
import { DEFAULT_RENDER_OPTIONS } from '../render/renderer';
import {
  DEFAULT_ARR_MS,
  DEFAULT_DAS_MS,
  DEFAULT_DCD_MS,
  DEFAULT_SOFT_DROP_FACTOR,
} from '../config';

export interface Settings {
  v: 1;
  audio: AudioSettings;
  postfx: PostFxSettings;
  render: RenderOptions;
  keymap: Record<Action, string[]>;
  paletteId: string;
  handling: {
    dasMs: number;
    arrMs: number;
    softDropFactor: number; // Infinity-friendly: -1 sentinel => instant
    dcdMs: number;
    /** Hard-drop guard window in ms after a new piece spawns. Hard-drop
     *  input within this window is ignored, preventing accidental
     *  slams from a held key. 0 disables. */
    hardDropGuardMs: number;
  };
  /** Legacy music variant — retained only so old saved settings don't break the JSON
   *  parse. Not used by the engine; replaced by the Radio model below. */
  musicVariant?: 'neon' | 'arcade' | 'ambient' | 'lofi' | 'silent';
  /** Display-side chat profanity filter (strong language → symbols). */
  chatFilter: boolean;
  /** Radio playlist state — see src/audio-howler/playlist.ts for track ids. */
  radio: { enabled: boolean };
  /** Optional render scale (1 = native CSS pixels; lower = cheaper to render). */
  renderScale: number;
  showFps: boolean;
  showFinesseHints: boolean;
  highFramerate: boolean; // attempt uncapped rendering
  countdownEnabled: boolean;
  /** Brief 3-2-1-go count when un-pausing mid-run so the player isn't
   *  dropped straight back into a falling piece. Independent of the
   *  match-start countdown above. */
  resumeCountdownEnabled: boolean;
  /** User-authored palettes, persisted on this profile. Each entry has the
   *  same shape as a built-in ThemePalette and an id prefixed with
   *  "custom:" so they don't collide with the static catalogue. */
  customPalettes: ThemePalette[];
}

export const DEFAULT_SETTINGS: Settings = {
  v: 1,
  audio: { ...DEFAULT_AUDIO_SETTINGS },
  postfx: { ...DEFAULT_POSTFX },
  render: { ...DEFAULT_RENDER_OPTIONS },
  keymap: structuredClone(DEFAULT_KEYMAP),
  paletteId: 'prism',
  handling: {
    dasMs: DEFAULT_DAS_MS,
    arrMs: DEFAULT_ARR_MS,
    softDropFactor: DEFAULT_SOFT_DROP_FACTOR,
    dcdMs: DEFAULT_DCD_MS,
    hardDropGuardMs: 0,
  },
  radio: { enabled: true },
  chatFilter: true,
  showFps: false,
  showFinesseHints: false,
  highFramerate: true,
  countdownEnabled: true,
  resumeCountdownEnabled: true,
  renderScale: 1,
  customPalettes: [],
};

// ─── Profile system ────────────────────────────────────────────────────
// Multiple users can share the same browser. Each profile gets its own
// settings and stats keyed by a sanitized profile id. The current profile
// id is tracked in a top-level localStorage entry.

const PROFILE_LIST_KEY = `${STORAGE_PREFIX}profiles`;
const ACTIVE_PROFILE_KEY = `${STORAGE_PREFIX}activeProfile`;
const LEGACY_SETTINGS_KEY = `${STORAGE_PREFIX}settings`;
const LEGACY_STATS_KEY = `${STORAGE_PREFIX}stats`;

export interface ProfileRecord {
  id: string;        // sanitized lowercase id (storage key)
  name: string;      // display name as the user typed it
  createdAt: number; // ms epoch
  lastPlayedAt: number;
  /** Guest profiles never persist to localStorage. Each session starts
   *  fresh, no records carry over. Useful for visitors / shared kiosks. */
  isGuest?: boolean;
}

interface ProfileList { v: 1; profiles: ProfileRecord[] }

function sanitizeProfileId(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9_-]/g, '_').slice(0, 32) || 'player';
}

export function listProfiles(): ProfileRecord[] {
  try {
    const raw = localStorage.getItem(PROFILE_LIST_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ProfileList;
    return Array.isArray(parsed.profiles) ? parsed.profiles : [];
  } catch { return []; }
}

function writeProfiles(list: ProfileRecord[]): void {
  try {
    localStorage.setItem(PROFILE_LIST_KEY, JSON.stringify({ v: 1, profiles: list }));
  } catch { /* quota */ }
}

export function getActiveProfileId(): string | null {
  try { return localStorage.getItem(ACTIVE_PROFILE_KEY); } catch { return null; }
}

export function setActiveProfile(id: string): void {
  try {
    localStorage.setItem(ACTIVE_PROFILE_KEY, id);
    const list = listProfiles();
    const idx = list.findIndex((p) => p.id === id);
    if (idx >= 0) {
      list[idx]!.lastPlayedAt = Date.now();
      writeProfiles(list);
    }
  } catch { /* quota */ }
}

/** Create a new profile. Returns the created record. If a profile with the
 *  sanitized id already exists, returns the existing one (idempotent). */
export function createProfile(name: string): ProfileRecord {
  const cleaned = name.trim();
  if (!cleaned) throw new Error('profile name is empty');
  const id = sanitizeProfileId(cleaned);
  const list = listProfiles();
  const existing = list.find((p) => p.id === id);
  if (existing) return existing;
  const rec: ProfileRecord = {
    id, name: cleaned.slice(0, 24), createdAt: Date.now(), lastPlayedAt: Date.now(),
  };
  list.push(rec);
  writeProfiles(list);
  return rec;
}

export function deleteProfile(id: string): void {
  const list = listProfiles().filter((p) => p.id !== id);
  writeProfiles(list);
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}p:${id}:settings`);
    localStorage.removeItem(`${STORAGE_PREFIX}p:${id}:stats`);
  } catch { /* */ }
  if (getActiveProfileId() === id) {
    try { localStorage.removeItem(ACTIVE_PROFILE_KEY); } catch { /* */ }
  }
}

/** Migrate any pre-profile data (`settle:settings` / `settle:stats`) onto
 *  the named profile. Used when a returning player first creates a
 *  profile after launching a previously-anonymous build. */
export function adoptLegacyDataIntoProfile(id: string): void {
  try {
    const legacySettings = localStorage.getItem(LEGACY_SETTINGS_KEY);
    if (legacySettings && !localStorage.getItem(profileSettingsKey(id))) {
      localStorage.setItem(profileSettingsKey(id), legacySettings);
    }
    const legacyStats = localStorage.getItem(LEGACY_STATS_KEY);
    if (legacyStats && !localStorage.getItem(profileStatsKey(id))) {
      localStorage.setItem(profileStatsKey(id), legacyStats);
    }
  } catch { /* */ }
}

function profileSettingsKey(id: string): string { return `${STORAGE_PREFIX}p:${id}:settings`; }
function profileStatsKey(id: string): string    { return `${STORAGE_PREFIX}p:${id}:stats`; }

// ─── Settings / stats accessors — namespaced by active profile ─────────

/** Sentinel id used while a guest session is active. Storage routes that
 *  see this id should no-op rather than read or write localStorage. */
export const GUEST_PROFILE_ID = '__guest__';

export function isGuestProfileId(id: string | null): boolean {
  return id === GUEST_PROFILE_ID;
}

function activeStorageKey(suffix: 'settings' | 'stats'): string | null {
  const id = getActiveProfileId();
  if (isGuestProfileId(id)) return null; // guest: no persistence
  if (!id) {
    // Fallback to legacy un-namespaced keys for pre-profile installs.
    return suffix === 'settings' ? LEGACY_SETTINGS_KEY : LEGACY_STATS_KEY;
  }
  return suffix === 'settings' ? profileSettingsKey(id) : profileStatsKey(id);
}

export function loadSettings(): Settings {
  try {
    const key = activeStorageKey('settings');
    if (!key) return structuredClone(DEFAULT_SETTINGS);
    const raw = localStorage.getItem(key);
    if (!raw) return structuredClone(DEFAULT_SETTINGS);
    const parsed = JSON.parse(raw);
    return mergeSettings(DEFAULT_SETTINGS, parsed);
  } catch {
    return structuredClone(DEFAULT_SETTINGS);
  }
}

/** Fires after every successful settings/stats save — main.ts hooks
 *  the cloud-sync scheduler here so every save path (screens, endRun,
 *  achievements) pushes without each call site knowing about it. */
let saveHook: (() => void) | null = null;
export function setSaveHook(cb: (() => void) | null): void {
  saveHook = cb;
}

export function saveSettings(s: Settings): void {
  try {
    const key = activeStorageKey('settings');
    if (!key) return; // guest: nothing persists
    localStorage.setItem(key, JSON.stringify(s));
    saveHook?.();
  } catch { /* quota / privacy */ }
}

function mergeSettings(base: Settings, overlay: Partial<Settings>): Settings {
  const out: Settings = structuredClone(base);
  if (!overlay || typeof overlay !== 'object') return out;
  if (overlay.audio) out.audio = { ...out.audio, ...overlay.audio };
  if (overlay.postfx) {
    // Spread first, then sanitize every numeric field — a stale persisted
    // shape can have undefined / null / NaN values that would propagate
    // through the filter chain as NaN-multiplied parameters, silently
    // disabling every visible effect even with the master toggle on.
    out.postfx = { ...out.postfx, ...overlay.postfx };
    const safeNum = (v: unknown, fallback: number): number =>
      typeof v === 'number' && Number.isFinite(v) ? v : fallback;
    out.postfx.crt            = safeNum(out.postfx.crt,            base.postfx.crt);
    out.postfx.curvature      = safeNum(out.postfx.curvature,      base.postfx.curvature);
    out.postfx.bloom          = safeNum(out.postfx.bloom,          base.postfx.bloom);
    out.postfx.bloomThreshold = safeNum(out.postfx.bloomThreshold, base.postfx.bloomThreshold);
    out.postfx.chromatic      = safeNum(out.postfx.chromatic,      base.postfx.chromatic);
    out.postfx.grain          = safeNum(out.postfx.grain,          base.postfx.grain);
    out.postfx.vignette       = safeNum(out.postfx.vignette,       base.postfx.vignette);
    out.postfx.glow           = safeNum(out.postfx.glow,           base.postfx.glow);
    out.postfx.scanlineSize   = safeNum(out.postfx.scanlineSize,   base.postfx.scanlineSize);
    if (typeof out.postfx.enabled !== 'boolean') out.postfx.enabled = base.postfx.enabled;
  }
  if (overlay.render) out.render = { ...out.render, ...overlay.render };
  if (overlay.handling) out.handling = { ...out.handling, ...overlay.handling };
  if (overlay.keymap) out.keymap = { ...out.keymap, ...overlay.keymap };
  if (overlay.paletteId) out.paletteId = overlay.paletteId;
  if (overlay.radio && typeof overlay.radio.enabled === 'boolean') {
    out.radio = { enabled: overlay.radio.enabled };
  }
  if (typeof overlay.chatFilter === 'boolean') out.chatFilter = overlay.chatFilter;
  if (typeof overlay.showFps === 'boolean') out.showFps = overlay.showFps;
  if (typeof overlay.showFinesseHints === 'boolean') out.showFinesseHints = overlay.showFinesseHints;
  if (typeof overlay.highFramerate === 'boolean') out.highFramerate = overlay.highFramerate;
  if (typeof overlay.countdownEnabled === 'boolean') out.countdownEnabled = overlay.countdownEnabled;
  if (typeof overlay.resumeCountdownEnabled === 'boolean') out.resumeCountdownEnabled = overlay.resumeCountdownEnabled;
  if (typeof overlay.renderScale === 'number') out.renderScale = overlay.renderScale;
  if (Array.isArray(overlay.customPalettes)) out.customPalettes = overlay.customPalettes;
  // Render scale is pinned to 1 while the option is pulled from the
  // video tab: non-1 values desync the viewport-rect math the DOM
  // overlays (trivia card etc.) position from. Normalizing here also
  // heals any save that still carries an old slider value.
  out.renderScale = 1;
  return out;
}

export interface PersistedStats {
  v: 1;
  pbs: Record<string, number>; // key -> value
  totalRuns: number;
  totalLines: number;
  totalPieces: number;
  totalTetrises: number;
  totalTspins: number;
  /** Full t-spins only (excludes minis). */
  totalTspinsFull: number;
  /** Mini t-spins only. */
  totalTspinsMini: number;
  /** T-spin singles (full, 1-line). */
  totalTspinSingles: number;
  /** T-spin doubles (full, 2-line). */
  totalTspinDoubles: number;
  /** T-spin triples (full, 3-line). */
  totalTspinTriples: number;
  totalPerfectClears: number;
  totalHardDrops: number;
  totalPlaytimeMs: number;
  /** Number of completed runs per mode (id → count). Used to gate
   *  "completed every mode" style achievements. */
  modeRuns: Partial<Record<string, number>>;
  /** Unlocked achievement ids. */
  achievements: string[];
}

export const DEFAULT_STATS: PersistedStats = {
  v: 1,
  pbs: {},
  totalRuns: 0,
  totalLines: 0,
  totalPieces: 0,
  totalTetrises: 0,
  totalTspins: 0,
  totalTspinsFull: 0,
  totalTspinsMini: 0,
  totalTspinSingles: 0,
  totalTspinDoubles: 0,
  totalTspinTriples: 0,
  totalPerfectClears: 0,
  totalHardDrops: 0,
  totalPlaytimeMs: 0,
  modeRuns: {},
  achievements: [],
};

export function loadStats(): PersistedStats {
  try {
    const key = activeStorageKey('stats');
    if (!key) return structuredClone(DEFAULT_STATS);
    const raw = localStorage.getItem(key);
    if (!raw) return structuredClone(DEFAULT_STATS);
    return normalizeStats(JSON.parse(raw));
  } catch {
    return structuredClone(DEFAULT_STATS);
  }
}

/** Coerce an untrusted stats blob (localStorage or cloud) into a fully
 *  populated PersistedStats. Same defaulting loadStats always applied —
 *  exported so the cloud-save reconciliation can normalize the remote
 *  side before comparing or merging. */
export function normalizeStats(parsed: unknown): PersistedStats {
  if (!parsed || typeof parsed !== 'object') return structuredClone(DEFAULT_STATS);
  const p = parsed as Partial<PersistedStats> & { pbs?: unknown; modeRuns?: unknown; achievements?: unknown };
  return {
    ...DEFAULT_STATS,
    ...(p as Partial<PersistedStats>),
    pbs: { ...((p.pbs && typeof p.pbs === 'object' ? p.pbs : {}) as Record<string, number>) },
    modeRuns: { ...((p.modeRuns && typeof p.modeRuns === 'object' ? p.modeRuns : {}) as Record<string, number>) },
    achievements: Array.isArray(p.achievements) ? (p.achievements as string[]).slice() : [],
  };
}

/** Coerce an untrusted settings blob into a fully populated Settings —
 *  the cloud-side twin of loadSettings' defaulting. */
export function normalizeSettings(parsed: unknown): Settings {
  if (!parsed || typeof parsed !== 'object') return structuredClone(DEFAULT_SETTINGS);
  return mergeSettings(DEFAULT_SETTINGS, parsed as Partial<Settings>);
}

/** True when the profile's saved data represents real play worth asking
 *  about before a cloud save is allowed to replace it: any completed
 *  runs / placed pieces, any personal best, any unlocked achievement,
 *  or a user-authored palette. */
export function hasLocalProgress(stats: PersistedStats, settings: Settings): boolean {
  return stats.totalRuns > 0
    || stats.totalPieces > 0
    || Object.keys(stats.pbs).length > 0
    || stats.achievements.length > 0
    || (settings.customPalettes?.length ?? 0) > 0;
}

/** Combine a local save with a cloud save so nothing meaningful is lost:
 *  lifetime totals take the larger side (max, not sum — the common
 *  conflict is a cloud save that was originally seeded FROM this same
 *  device, where summing would double-count every counter), personal
 *  bests keep the better value per key (lower wins for time-ranked
 *  keys), achievements union, per-mode run counts take the max, and
 *  settings keep the local side (what the player is currently looking
 *  at) with custom palettes unioned by id. */
export function mergeSaves(
  localSettings: Settings,
  localStats: PersistedStats,
  cloud: { settings?: unknown; stats?: unknown },
  lowerBetterPbKeys: ReadonlySet<string>,
): { settings: Settings; stats: PersistedStats } {
  const cloudStats = normalizeStats(cloud.stats);
  const cloudSettings = normalizeSettings(cloud.settings);

  const stats: PersistedStats = structuredClone(localStats);
  const numericKeys = [
    'totalRuns', 'totalLines', 'totalPieces', 'totalTetrises', 'totalTspins',
    'totalTspinsFull', 'totalTspinsMini', 'totalTspinSingles', 'totalTspinDoubles',
    'totalTspinTriples', 'totalPerfectClears', 'totalHardDrops', 'totalPlaytimeMs',
  ] as const;
  for (const k of numericKeys) stats[k] = Math.max(localStats[k] ?? 0, cloudStats[k] ?? 0);
  for (const [k, v] of Object.entries(cloudStats.pbs)) {
    const cur = stats.pbs[k];
    if (cur == null) { stats.pbs[k] = v; continue; }
    stats.pbs[k] = lowerBetterPbKeys.has(k) ? Math.min(cur, v) : Math.max(cur, v);
  }
  for (const [k, v] of Object.entries(cloudStats.modeRuns)) {
    stats.modeRuns[k] = Math.max(stats.modeRuns[k] ?? 0, v ?? 0);
  }
  stats.achievements = [...new Set([...localStats.achievements, ...cloudStats.achievements])];

  const settings: Settings = structuredClone(localSettings);
  const haveIds = new Set(settings.customPalettes.map((p) => p.id));
  for (const p of cloudSettings.customPalettes) {
    if (!haveIds.has(p.id)) settings.customPalettes.push(p);
  }
  return { settings, stats };
}

/** Adopt a cloud save: writes the blobs to the active profile's keys
 *  WITHOUT firing the save hook (which would immediately push the
 *  same data back up). Caller reloads via loadSettings/loadStats. */
export function applyCloudSave(data: { settings?: unknown; stats?: unknown }): void {
  try {
    const sk = activeStorageKey('settings');
    const tk = activeStorageKey('stats');
    if (sk && data.settings && typeof data.settings === 'object') {
      localStorage.setItem(sk, JSON.stringify(data.settings));
    }
    if (tk && data.stats && typeof data.stats === 'object') {
      localStorage.setItem(tk, JSON.stringify(data.stats));
    }
  } catch { /* quota */ }
}

export function saveStats(s: PersistedStats): void {
  try {
    const key = activeStorageKey('stats');
    if (!key) return; // guest: don't persist
    localStorage.setItem(key, JSON.stringify(s));
    saveHook?.();
  } catch { /* */ }
}
